import { bind, wire } from 'hyperhtml';

import { RenderController } from '../base';
import TeachersSelect from '../../wires/TeachersSelect';
import { ChangeEvent } from 'react';

const times = [
  '00:00', '00:15', '00:30', '00:45',
  '01:00', '01:15', '01:30', '01:45',
  '02:00', '02:15', '02:30', '02:45',
  '03:00', '03:15', '03:30', '03:45',
  '04:00', '04:15', '04:30', '04:45',
  '05:00', '05:15', '05:30', '05:45',
  '06:00', '06:15', '06:30', '06:45',
  '07:00', '07:15', '07:30', '07:45',
  '08:00', '08:15', '08:30', '08:45',
  '09:00', '09:15', '09:30', '09:45',
  '10:00', '10:15', '10:30', '10:45',
  '11:00', '11:15', '11:30', '11:45',
  '12:00', '12:15', '12:30', '12:45',
  '13:00', '13:15', '13:30', '13:45',
  '14:00', '14:15', '14:30', '14:45',
  '15:00', '15:15', '15:30', '15:45',
  '16:00', '16:15', '16:30', '16:45',
  '17:00', '17:15', '17:30', '17:45',
  '18:00', '18:15', '18:30', '18:45',
  '19:00', '19:15', '19:30', '19:45',
  '20:00', '20:15', '20:30', '20:45',
  '21:00', '21:15', '21:30', '21:45',
  '22:00', '22:15', '22:30', '22:45',
  '23:00', '23:15', '23:30', '23:45',
];

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const locations = ['Home', 'Office', 'Other', 'Online'];

interface LessonTimeInfo {
  lessonDayOfWeek: string;
  lessonStartTime: string;
  lessonEndTime: string;
  lessonLocation: string;
  teacherId?: number;
}

interface TeacherInfo {
  id: number;
  displayNameZh: string;
  profileImageUrlSquare: string;
  isInactive: boolean;
}

interface State {
  lessonTimesMap: { [key: string]: LessonTimeInfo };
  curFormIndex: number;
  regularTeacherIds: number[];
  allTeachers: TeacherInfo[];
}

export default class LessonTimesFormController extends RenderController {
  static targets = ['lessonTimes', 'defaultLessonLength', 'selectPicker'];

  hasDefaultLessonLengthTarget: boolean;
  excludeLocation: boolean;
  excludeTeacher: boolean;
  defaultLessonLengthTarget: HTMLInputElement;
  lessonTimesTarget: HTMLElement;
  selectPickerTargets: HTMLSelectElement[];

  state: State = {
    lessonTimesMap: {},
    curFormIndex: 0,
    regularTeacherIds: [],
    allTeachers: [],
  };

  connect() {
    const lessonTimesMap: { [key: string]: LessonTimeInfo } = {};
    const lessonTimes: LessonTimeInfo[] = JSON.parse(this.data.get('lessonTimes'));
    lessonTimes.forEach((lessonTime, index) => {
      lessonTimesMap[index] = lessonTime;
    });
    this.excludeTeacher = this.data.get('excludeTeacher') === 'true';
    this.excludeLocation = this.data.get('excludeLocation') === 'true';
    const allTeachers = this.excludeTeacher ? [] : JSON.parse(this.data.get('allTeachers'));
    const regularTeacherIds = this.excludeTeacher ? [] : JSON.parse(this.data.get('regularTeacherIds'));
    this.setState({
      allTeachers,
      lessonTimesMap,
      regularTeacherIds,
      curFormIndex: Object.keys(lessonTimesMap).length,
    });

    this.addEventListener('CHANGE_REGULAR_TEACHERS', (evt: Event, updatedIds: number[]) => {
      this.onChangeRegularTeachers(updatedIds);
    });
  }

  onChangeRegularTeachers = (regularTeacherIds: number[]) => {
    const lessonTimesMap = { ...this.state.lessonTimesMap };
    Object.keys(lessonTimesMap).forEach((formIndex) => {
      const lessonTime = lessonTimesMap[formIndex];
      if (regularTeacherIds.indexOf(lessonTime.teacherId) === -1) {
        lessonTimesMap[formIndex] = { ...lessonTime, teacherId: null };
      }
    });
    this.setState({ regularTeacherIds, lessonTimesMap });
  }

  get lessonTimes() {
    return Object.values(this.state.lessonTimesMap);
  }

  getDefaultLessonEndTime(startTime: string) {
    const hourAndMin = startTime.split(':');

    const startDate = new Date();
    startDate.setHours(parseInt(hourAndMin[0], 10));
    startDate.setMinutes(parseInt(hourAndMin[1], 10));

    let minutesIncrement = 120;
    if (this.hasDefaultLessonLengthTarget) {
      minutesIncrement = parseInt(this.defaultLessonLengthTarget.value, 10);
    }

    const endDate = new Date(startDate.getTime() + minutesIncrement * 60 * 1000);

    const formattedHours = (`0${endDate.getHours()}`).slice(-2);
    const formattedMinutes = (`0${endDate.getMinutes()}`).slice(-2);
    const formattedTime = `${formattedHours}:${formattedMinutes}`;

    if (times.indexOf(formattedTime) === -1) {
      return times[times.length - 1];
    }
    return formattedTime;
  }

  getLast(field: string, defaultVal: string): string {
    if (this.lessonTimes.length === 0) return defaultVal;
    return (this.lessonTimes[this.lessonTimes.length - 1] as any)[field];
  }

  onAddLessonTime() {
    const lastLessonDay = this.getLast('lessonDayOfWeek', 'Sunday');
    const lessonDayOfWeek = days[(days.indexOf(lastLessonDay) + 1) % 7];
    const lessonStartTime = this.getLast('lessonStartTime', '09:00');
    let lessonEndTime = this.getLast('lessonEndTime', '11:00');
    if (this.hasDefaultLessonLengthTarget) {
      lessonEndTime = this.getDefaultLessonEndTime(lessonStartTime);
    }

    const lessonTimesMap = { ...this.state.lessonTimesMap };
    const lessonLocation = this.getLast('lessonLocation', 'Other');
    lessonTimesMap[this.state.curFormIndex] = {
      lessonDayOfWeek,
      lessonStartTime,
      lessonEndTime,
      lessonLocation,
    };
    this.setState({ lessonTimesMap, curFormIndex: this.state.curFormIndex + 1 });
  }

  onDeleteLessonTime(formIndex: string | number) {
    const lessonTimesMap = { ...this.state.lessonTimesMap };
    delete lessonTimesMap[formIndex];
    this.setState({ lessonTimesMap });
  }

  renderTimeOptions(selectedTime: string) {
    const timeOptions: string[] = [];
    times.forEach((time) => {
      timeOptions.push(wire()`<option value=${time} selected=${time === selectedTime}>${time}</option>`);
      if (time === '12:00' || time === '17:00') {
        timeOptions.push(wire()`<option disabled="disabled">------</option>`);
      }
    });
    return timeOptions;
  }

  renderDayOptions(selectedDay: string) {
    return days.map(day => wire()`<option value=${day} selected=${day === selectedDay}>${day}</option>`);
  }

  renderLocationOptions(selectedLoc: string) {
    return locations.map(loc => wire()`<option value=${loc} selected=${loc === selectedLoc}>${loc}</option>`);
  }

  onUpdateValue = (formIndex: string | number, field: string, evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.currentTarget;
    const lessonTimesMap = { ...this.state.lessonTimesMap };

    (lessonTimesMap[formIndex] as any)[field] = value;
    if (field === 'lessonStartTime' && this.hasDefaultLessonLengthTarget) {
      const endTime = this.getDefaultLessonEndTime(value);
      lessonTimesMap[formIndex].lessonEndTime = endTime;
    }
    this.setState({ lessonTimesMap });
  }

  onSelectTeacher = (teacherId: number, formIndex: number) => {
    const lessonTimesMap = { ...this.state.lessonTimesMap };
    lessonTimesMap[formIndex].teacherId = teacherId;
    this.setState({ lessonTimesMap });
  }

  renderTeacherSelect(formIndex: string | number) {
    const { lessonTimesMap, regularTeacherIds, allTeachers } = this.state;
    const lessonTime = lessonTimesMap[formIndex];
    const regularTeachers = regularTeacherIds.map(id => (
      allTeachers.find(teacher => teacher.id === id)
    ));
    return TeachersSelect({
      teachers: regularTeachers,
      selectedId: lessonTime.teacherId,
      onChange: evt => this.onSelectTeacher(parseInt(evt.currentTarget.value, 10), formIndex as number),
      name: `lesson_times-${formIndex}-teacher`,
      target: `${this.identifier}.selectPicker`,
      includeEmpty: true,
    });
  }

  renderTeacherCol(formIndex: string | number) {
    if (this.excludeTeacher) return null;
    return wire()`
      <div class="col-3 teacher_select">
        ${this.renderTeacherSelect(formIndex)}
      </div>
    `;
  }

  renderLocationCol(formIndex: string | number) {
    if (this.excludeLocation) return null;
    const lessonTime = this.state.lessonTimesMap[formIndex];
    return wire()`
      <div class="col-2">
        <select
          class="form-control"
          onChange=${(evt: ChangeEvent<HTMLInputElement>) => this.onUpdateValue(formIndex, 'lessonLocation', evt)}
          name=${`lesson_times-${formIndex}-lesson_location`}
          required=True
        >
          ${this.renderLocationOptions(lessonTime.lessonLocation)}
        </select>
      </div>
    `;
  }

  render() {
    const formIndices = Object.keys(this.state.lessonTimesMap);
    const lessonTimes = formIndices.map((formIndex) => {
      const lessonTime = this.state.lessonTimesMap[formIndex];
      return wire()`
      <div class="form-group">
        <div class="row admin">
          <div class="col-2">
            <select
              class="form-control"
              onChange=${(evt: ChangeEvent<HTMLInputElement>) => this.onUpdateValue(formIndex, 'lessonDayOfWeek', evt)}
              name=${`lesson_times-${formIndex}-lesson_day_of_week`}
            >
              ${this.renderDayOptions(lessonTime.lessonDayOfWeek)}
            </select>
          </div>
          <div class="col-2">
            <select
              class="form-control"
              onChange=${(evt: ChangeEvent<HTMLInputElement>) => this.onUpdateValue(formIndex, 'lessonStartTime', evt)}
              name=${`lesson_times-${formIndex}-lesson_start_time`}
            >
              ${this.renderTimeOptions(lessonTime.lessonStartTime)}
            </select>
          </div>
          <div class="col-2">
            <select
              class="form-control"
              onChange=${(evt: ChangeEvent<HTMLInputElement>) => this.onUpdateValue(formIndex, 'lessonEndTime', evt)}
              name=${`lesson_times-${formIndex}-lesson_end_time`}
            >
              ${this.renderTimeOptions(lessonTime.lessonEndTime)}
            </select>
          </div>
          ${this.renderLocationCol(formIndex)}
          ${this.renderTeacherCol(formIndex)}
          <div class="col-1">
            <button onClick=${() => this.onDeleteLessonTime(formIndex)} data-form-index=${formIndex} class="btn btn-danger">
              <i class="fa fa-minus-circle"></i>
            </button>
          </div>
        </div>
      </div>
      `;
    });
    bind(this.lessonTimesTarget)`${lessonTimes}`;
    ($(this.selectPickerTargets) as any).selectpicker('render');
  }
}
